import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';

const PreferencesSection = () => {
  const [budgetRange, setBudgetRange] = useState([25000, 75000]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);

  const serviceCategories = [
    { id: 'photography', label: 'Photography', icon: 'Camera' },
    { id: 'videography', label: 'Videography', icon: 'Video' },
    { id: 'catering', label: 'Catering', icon: 'ChefHat' },
    { id: 'dj', label: 'DJ/Music', icon: 'Music' },
    { id: 'decorations', label: 'Decorations', icon: 'Flower' },
    { id: 'makeup', label: 'Makeup', icon: 'Palette' },
    { id: 'venue', label: 'Venue', icon: 'Building' },
    { id: 'transportation', label: 'Transportation', icon: 'Car' },
    { id: 'flowers', label: 'Flowers', icon: 'Flower2' },
    { id: 'planning', label: 'Planning', icon: 'Calendar' },
    { id: 'jewelry', label: 'Jewelry', icon: 'Gem' },
    { id: 'invitations', label: 'Invitations', icon: 'Mail' }
  ];

  const weddingStyles = [
    { id: 'traditional', label: 'Traditional', color: 'bg-primary-100 text-primary' },
    { id: 'modern-fusion', label: 'Modern Fusion', color: 'bg-secondary-100 text-secondary-600' },
    { id: 'bollywood-glam', label: 'Bollywood Glam', color: 'bg-warning-100 text-warning-600' },
    { id: 'minimalist', label: 'Minimalist Elegance', color: 'bg-surface-200 text-text-primary' },
    { id: 'vintage', label: 'Vintage Classic', color: 'bg-success-100 text-success-600' },
    { id: 'rustic', label: 'Rustic Charm', color: 'bg-orange-100 text-orange-600' },
    { id: 'destination', label: 'Destination', color: 'bg-blue-100 text-blue-600' },
    { id: 'luxury', label: 'Luxury Royal', color: 'bg-purple-100 text-purple-600' }
  ];

  const handleBudgetChange = (index, value) => {
    const newRange = [...budgetRange];
    newRange[index] = parseInt(value);
    setBudgetRange(newRange);
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const toggleStyle = (styleId) => {
    setSelectedStyles(prev => 
      prev.includes(styleId) 
        ? prev.filter(id => id !== styleId)
        : [...prev, styleId]
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Preferences</h1>
          <p className="text-text-muted mt-1">Customize your wedding preferences and budget</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          iconName="Save"
        >
          Save
        </Button>
      </div>

      {/* Budget Range Section */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="DollarSign" size={20} color="#8B1538" />
          <h2 className="text-lg font-semibold text-text-primary">Budget Range</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-text-muted">
            <span>Total Wedding Budget</span>
            <span>{formatCurrency(budgetRange[0])} - {formatCurrency(budgetRange[1])}</span>
          </div>
          
          {/* Budget Range Slider */}
          <div className="relative">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <div className="h-2 bg-surface-200 rounded-full">
                  <div 
                    className="h-2 bg-gradient-to-r from-primary to-secondary rounded-full"
                    style={{
                      marginLeft: `${(budgetRange[0] / 200000) * 100}%`,
                      width: `${((budgetRange[1] - budgetRange[0]) / 200000) * 100}%`
                    }}
                  />
                </div>
                <input
                  type="range"
                  min="5000"
                  max="200000"
                  step="5000"
                  value={budgetRange[0]}
                  onChange={(e) => handleBudgetChange(0, e.target.value)}
                  className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
                />
                <input
                  type="range"
                  min="5000"
                  max="200000"
                  step="5000"
                  value={budgetRange[1]}
                  onChange={(e) => handleBudgetChange(1, e.target.value)}
                  className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Minimum Budget
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted">$</span>
                <input
                  type="number"
                  min="5000"
                  max="200000"
                  step="5000"
                  value={budgetRange[0]}
                  onChange={(e) => handleBudgetChange(0, e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Maximum Budget
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted">$</span>
                <input
                  type="number"
                  min="5000"
                  max="200000"
                  step="5000"
                  value={budgetRange[1]}
                  onChange={(e) => handleBudgetChange(1, e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>$5,000</span>
            <span>$200,000+</span>
          </div>
        </div>
      </div>

      {/* Service Interests Section */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Heart" size={20} color="#8B1538" />
          <h2 className="text-lg font-semibold text-text-primary">Service Interests</h2>
        </div>
        
        <p className="text-sm text-text-muted mb-4">
          Select the services you're interested in for your wedding
        </p>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {serviceCategories.map((service) => (
            <button
              key={service.id}
              onClick={() => toggleService(service.id)}
              className={`flex items-center space-x-2 p-3 rounded-lg border transition-all ${
                selectedServices.includes(service.id)
                  ? 'bg-primary-50 border-primary text-primary' :'bg-surface-100 border-border text-text-secondary hover:bg-surface-200'
              }`}
            >
              <Icon 
                name={service.icon} 
                size={18} 
                color={selectedServices.includes(service.id) ? '#8B1538' : 'currentColor'} 
              />
              <span className="text-sm font-medium">{service.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-muted">
            {selectedServices.length} services selected
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedServices([])}
          >
            Clear All
          </Button>
        </div>
      </div>

      {/* Wedding Style Tags Section */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Palette" size={20} color="#8B1538" />
          <h2 className="text-lg font-semibold text-text-primary">Wedding Style Tags</h2>
        </div>
        
        <p className="text-sm text-text-muted mb-4">
          Choose styles that reflect your vision and cultural preferences
        </p>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {weddingStyles.map((style) => (
            <button
              key={style.id}
              onClick={() => toggleStyle(style.id)}
              className={`p-3 rounded-lg border transition-all text-center ${
                selectedStyles.includes(style.id)
                  ? `${style.color} border-current`
                  : 'bg-surface-100 border-border text-text-secondary hover:bg-surface-200'
              }`}
            >
              <span className="text-sm font-medium">{style.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-muted">
            {selectedStyles.length} styles selected
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedStyles([])}
          >
            Clear All
          </Button>
        </div>
      </div>

      {/* Cultural Preferences */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Globe" size={20} color="#8B1538" />
          <h2 className="text-lg font-semibold text-text-primary">Cultural Preferences</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Language Preferences
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {['English', 'Hindi', 'Punjabi', 'Urdu', 'Tamil', 'Telugu', 'Gujarati', 'Bengali'].map((language) => (
                <label key={language} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-surface-100 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary-200"
                  />
                  <span className="text-sm">{language}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Dietary Preferences
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {['Vegetarian', 'Vegan', 'Halal', 'Kosher', 'Jain', 'No Restrictions'].map((diet) => (
                <label key={diet} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-surface-100 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary-200"
                  />
                  <span className="text-sm">{diet}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 bg-surface rounded-xl p-6 border border-border">
        <div className="text-center sm:text-left">
          <p className="text-sm text-text-muted">
            Your preferences help us recommend the perfect vendors for your wedding
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="ghost"
            size="sm"
          >
            Reset
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconName="Save"
          >
            Save Preferences
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default PreferencesSection;